import React, { useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePageAccess } from "../hooks/usePageAccess";
import { HotelContext } from "../context/HotelContext";
import { useAuth } from "react-oidc-context";
import { getV2FallbackPath } from "../utils/v2Navigation";

/**
 * Composant de garde pour protéger les routes basées sur l'accès aux pages
 */
const PageAccessGuard = ({
  pageName,
  children,
  fallbackPath = "/hotel/lunishower/v2/dashboard",
  requireHotel = true,
  bypass = false,
}) => {
  const { hId } = useContext(HotelContext);
  const auth = useAuth();
  const location = useLocation();
  const [contextInitialized, setContextInitialized] = useState(false);

  useEffect(() => {
    if (!requireHotel) {
      setContextInitialized(true);
      return;
    }

    if (auth.isLoading) {
      return;
    }

    const storedHId = localStorage.getItem("hId");
    if (hId || (!storedHId && !auth.isAuthenticated)) {
      setContextInitialized(true);
      return;
    }

    if (!hId && storedHId) {
      const timer = setTimeout(() => {
        setContextInitialized(true);
      }, 200);
      return () => clearTimeout(timer);
    }

    setContextInitialized(true);
  }, [auth.isLoading, auth.isAuthenticated, requireHotel, hId]);

  const settingsPages = ["hotelInformations", "rooms_settings"];
  const isSettingsPage = settingsPages.includes(pageName);

  const { hasAccess, loading } = usePageAccess(pageName, contextInitialized ? hId : null, {
    redirectOnDenied: false,
    redirectPath: fallbackPath,
    requireHotel: requireHotel && contextInitialized,
  });

  if (bypass) {
    return <>{children}</>;
  }

  if (!contextInitialized || auth.isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div>Chargement...</div>
      </div>
    );
  }

  if (isSettingsPage && hId) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div>Chargement...</div>
      </div>
    );
  }

  if (!hasAccess) {
    if (!hId && requireHotel) {
      return <Navigate to="/user/hotels" replace />;
    }

    const returnTo = getV2FallbackPath(location.state);
    return <Navigate to={returnTo} replace state={{ accessDenied: true }} />;
  }

  return <>{children}</>;
};

export default PageAccessGuard;
